import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { EditLockService } from '../../../features/editor/services/edit-lock.service';

/**
 * Persistent banner at the top of the editor, shown whenever the project is
 * read-only. Two reasons, distinct messages:
 *   - imported project (`sourceUrl !== 'local'`) — edit at the source
 *   - another user holds the edit lock (spec/plans/edit-locks/) — with a
 *     Take-over action when the lock is stale (anyone) or the viewer is an admin
 *
 * All other read-only affordances key off `EditorStateService.isReadOnly()`, so
 * the banner appears exactly when forms/Monaco/timeline are locked down.
 */
@Component({
	selector: 'app-readonly-banner',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		@if (state.isImportedReadOnly()) {
			<div class="readonly-banner" role="status">
				<i class="pi pi-lock"></i>
				<span class="message">
					<strong>Imported project — read-only.</strong>
					Changes must be made at the source.
				</span>
			</div>
		} @else if (lock.lockedByOther()) {
			<div class="readonly-banner locked" role="status">
				<i class="pi" [class.pi-lock]="!lock.isStale()" [class.pi-lock-open]="lock.isStale()"></i>
				<span class="message">
					@if (lock.isStale()) {
						<strong>{{ lock.holderName() }} was editing, but the lock has expired.</strong>
						Read-only until you take over.
					} @else {
						<strong>{{ lock.holderName() }} is currently editing.</strong>
						This project is read-only.
					}
				</span>
				@if (lock.canTakeOver()) {
					<button type="button" class="takeover-btn" (click)="onTakeOver()">Take over editing</button>
				}
			</div>
		}
	`,
	styleUrl: './readonly-banner.scss',
})
export class ReadonlyBannerComponent {
	protected state = inject(EditorStateService);
	protected lock = inject(EditLockService);
	private confirm = inject(ConfirmationService);

	/**
	 * A stale lock is low-stakes — take it over directly. Forcing an *active*
	 * lock (admin only) evicts a colleague mid-edit, so confirm first.
	 */
	protected onTakeOver(): void {
		if (this.lock.isStale()) {
			this.lock.takeOver();
			return;
		}
		this.confirm.confirm({
			header: 'Take over editing?',
			message: `${this.lock.holderName()} is currently editing this project. Taking over switches them to read-only and may discard their unsaved changes.`,
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: 'Take over',
			rejectLabel: 'Cancel',
			accept: () => this.lock.takeOver(),
		});
	}
}
